import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import { APR_MAR, BOTH, JAN_DEC } from "constants/calendarTypes";
import { PAID_TASK, UNPAID_TASK } from "constants/task";
import dayjs from "dayjs";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useEffect, useState, useRef } from "react";
import { MultiValue, SingleValue } from "react-select";
import { debounce } from "utils/debouncing";
import { getSeasonsFrom } from "utils/getAllSeason";
import {
  Task1120TrackerFilter,
  Task1120TrackerFilterType,
} from "./Task1120TrackerFilter";
import { Button } from "components/DesignSystem/Button/Button";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import {
  ENTITY_TAXED_AS_OPTIONS,
  FILTER_ENTITY_TAXED_AS,
  FILTER_PROFITABLE_COMPANY_TAG,
  NON_PROFIT_501C3,
} from "./Task1120FilterOptions";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { useLazyGetTask1120TrackerQuery } from "store/apis/Task1120Tracker";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { DownloadIcon } from "components/icons/Download";
import { Chip } from "components/DesignSystem/Chips/Chips";
import { Task1120BulkAction } from "./Task1120BulkAction";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Loader from "components/DesignSystem/Loader/loader";
import DoubleGreenTick from "static/images/DoubleGreenTick.svg";
import { useRoleBasedView } from "hooks/useRoleBasedView";

export const TaskTrackerHeader = ({
  totalPage = 0,
  itemsPerPage = 0,
  totalItemCount = 0,
  values,
  updateFilter,
  resetFilter,
  filters,
  isSuccess,
  isUpdating,
}: {
  totalPage?: number;
  itemsPerPage?: number;
  totalItemCount?: number;
  resetFilter: () => void;
  filters: string;
  isSuccess: boolean;
  isUpdating: boolean;
} & Task1120TrackerFilterType) => {
  const query = useQuery();
  const page = Number(query.get("page") || "1");
  const selectedTab = query.get("tab") || BOTH;
  const searchTerm = query.get("search_term") || null;
  const selectedSeason =
    query.get("season") || dayjs().subtract(1, "year").year();
  const selectedCalendarYear = query.get("calendar_year") || BOTH;
  let nextYear = dayjs().add(1, "year").year();
  const { data } = useGetCountriesDataQuery();
  const US_FORMATION_TYPE =
    data?.find(
      ({ name, code_alpha_2 }) =>
        code_alpha_2 === "US" && name === "United States"
    )?.formation_type || [];
  const { successToast, alertToast } = useToast();
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { update, updateMultiple } = useUpdateQuery();
  const [getAllTask1120, { isFetching }] = useLazyGetTask1120TrackerQuery();

  const downloadCSV = async (exportToMail = true) => {
    try {
      const res = await getAllTask1120({
        ...JSON.parse(filters),
        generate_report: true,
        export_to_mail: exportToMail,
      }).unwrap();

      if (res?.download_url) {
        window.open(res?.download_url);
      }

      successToast({
        message: exportToMail
          ? "Filing report has been sent to your email"
          : "Filing report downloaded successfully",
      });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const resetPageAndUpdateFilter = (
    ...props: Parameters<typeof updateFilter>
  ) => {
    update({
      query: "page",
      value: "1",
    });
    updateFilter(...props);
  };

  const handleTabChange = (value: string) => {
    if (value) {
      updateMultiple([
        {
          query: "tab",
          value: value !== BOTH ? value : null,
        },
        {
          query: "page",
          value: "1",
        },
      ]);
    }
  };

  const handleSearch = debounce((e) => {
    updateMultiple([
      {
        query: "search_term",
        value: e.target.value || null,
      },
      {
        query: "page",
        value: "1",
      },
    ]);
  });

  const handleSeasonChange = (season?: string) => {
    updateMultiple([
      {
        query: "season",
        value: season || null,
      },
      {
        query: "page",
        value: "1",
      },
    ]);
  };

  const handleCalendarYearChange = (calendarYear?: string) => {
    updateMultiple([
      {
        query: "calendar_year",
        value: calendarYear || null,
      },
      {
        query: "page",
        value: "1",
      },
    ]);
  };

  const handleFormationTypeChange = (
    selectedOption: SingleValue<OptionData> | MultiValue<OptionData>
  ) => {
    if (Array.isArray(selectedOption)) {
      return null;
    }

    resetPageAndUpdateFilter(
      "FORMATION_TYPE",
      (selectedOption as SingleValue<OptionData>)?.value || ""
    );
    updateFilter(FILTER_ENTITY_TAXED_AS, null);
  };

  const seasonOptions = getSeasonsFrom(2019, nextYear).map((year) => ({
    label: year.toString(),
    value: year.toString(),
  }));

  const calendarYearOptions = [
    {
      label: "Both",
      value: BOTH,
    },
    {
      label: "Calendar Year",
      value: JAN_DEC,
    },
    {
      label: "Fiscal Year",
      value: APR_MAR,
    },
  ];

  const defaultCalendarYear = selectedCalendarYear
    ? calendarYearOptions.find(({ value }) => value === selectedCalendarYear)
    : calendarYearOptions[0];

  const isAnyFilterActive = Object.values(values).some((value) =>
    Array.isArray(value) ? value.length > 0 : Boolean(value)
  );

  const formationTypeOptions = [
    {
      value: "",
      label: "All",
    },
    ...US_FORMATION_TYPE.filter(({ name }) => name !== NON_PROFIT_501C3).map(
      ({ name, uuid }) => ({
        value: uuid,
        label: name,
      })
    ),
  ];

  const selectedFormationType =
    values.FORMATION_TYPE || values.FORMATION_TYPE == ""
      ? formationTypeOptions.find(
          (option) => values.FORMATION_TYPE === option.value
        )
      : [];

  const llcFormationTypeUuid = process.env.PUBLIC_ENTITY_TYPE_LLC;
  const isLLCSelected =
    !!llcFormationTypeUuid && values.FORMATION_TYPE === llcFormationTypeUuid;

  const selectedEntityTaxedAs = ENTITY_TAXED_AS_OPTIONS.find(
    (opt) => opt.value === (values.ENTITY_TAXED_AS || "")
  );

  const handleEntityTaxedAsChange = (
    selectedOption: SingleValue<OptionData> | MultiValue<OptionData>
  ) => {
    if (Array.isArray(selectedOption)) {
      return;
    }
    resetPageAndUpdateFilter(
      FILTER_ENTITY_TAXED_AS,
      (selectedOption as SingleValue<OptionData>)?.value || null
    );
  };

  useEffect(() => {
    if (isUpdating) {
      setShowSavedMessage(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    } else if (isSuccess && showSavedMessage) {
      timeoutRef.current = setTimeout(() => {
        setShowSavedMessage(false);
      }, 10000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isUpdating, isSuccess, showSavedMessage]);

  const { isCpa, isAdmin, isForeignCA } = useRoleBasedView();
  const isCpaOnly = isCpa && !isAdmin && !isForeignCA;

  return (
    <div className="t-space-y-4">
      <div className="t-flex t-items-center t-justify-between">
        <div className="t-flex t-gap-4 t-items-center">
          {!isCpaOnly && (
            <ToggleGroup
              size="small"
              onValueChange={handleTabChange}
              value={selectedTab || BOTH}
            >
              <ToggleGroupItem value={BOTH}>All</ToggleGroupItem>
              <ToggleGroupItem value={PAID_TASK}>Paid</ToggleGroupItem>
              <ToggleGroupItem value={UNPAID_TASK}>Unpaid</ToggleGroupItem>
            </ToggleGroup>
          )}
          <Search
            customSize="small"
            placeholder="Search.."
            className="t-w-52"
            onChange={handleSearch}
            defaultValue={searchTerm || ""}
          />
        </div>
        <div className="t-flex t-gap-4 t-items-center">
          {!isCpaOnly && (
            <Chip
              onRemove={() =>
                resetPageAndUpdateFilter(FILTER_PROFITABLE_COMPANY_TAG, null)
              }
              isActive={Boolean(values.PROFITABLE_COMPANY_TAG)}
              filterType={FILTER_PROFITABLE_COMPANY_TAG}
              onClick={() =>
                resetPageAndUpdateFilter(
                  FILTER_PROFITABLE_COMPANY_TAG,
                  process.env.PUBLIC_COMPANY_GROUP_PROFITABLE_TAG_ID || ""
                )
              }
              isFixedFilter
            >
              Profitable
            </Chip>
          )}

          <div className="t-w-40">
            <Combobox
              size="small"
              components={{
                ClearIndicator: () => null,
              }}
              placeholder="Formation type"
              value={selectedFormationType}
              options={formationTypeOptions}
              menuPortalTarget={document.body}
              maxMenuHeight={220}
              onChange={handleFormationTypeChange}
              block
              styles={{
                menu: (base) => ({
                  ...base,
                  width: "230px",
                }),
              }}
            />
          </div>
          {isLLCSelected && (
            <div className="t-w-48">
              <Combobox
                size="small"
                components={{
                  ClearIndicator: () => null,
                }}
                placeholder="Entity taxed as"
                value={selectedEntityTaxedAs}
                options={ENTITY_TAXED_AS_OPTIONS}
                menuPortalTarget={document.body}
                onChange={handleEntityTaxedAsChange}
                block
              />
            </div>
          )}
          <div className="t-w-40">
            <Combobox
              size="small"
              components={{
                ClearIndicator: () => null,
              }}
              value={defaultCalendarYear}
              options={calendarYearOptions}
              menuPortalTarget={document.body}
              onChange={(
                option: SingleValue<OptionData> | MultiValue<OptionData>
              ) =>
                handleCalendarYearChange(
                  (option as SingleValue<OptionData>)?.value
                )
              }
              block
            />
          </div>
          <Combobox
            size="small"
            components={{
              ClearIndicator: () => null,
            }}
            value={{
              label: selectedSeason.toString(),
              value: selectedSeason.toString(),
            }}
            options={seasonOptions}
            menuPortalTarget={document.body}
            onChange={(
              option: SingleValue<OptionData> | MultiValue<OptionData>
            ) => handleSeasonChange((option as SingleValue<OptionData>)?.value)}
          />
          <Dropdown.Root>
            <Dropdown.Trigger asChild className="t-group">
              <div>
                <Button
                  customType="icon"
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  isLoading={isFetching}
                  disabled={isFetching}
                >
                  <div className="t-flex t-items-center t-gap-1.5 t-text-text-30">
                    <DownloadIcon color="currentColor" />
                  </div>
                </Button>
              </div>
            </Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Content sideOffset={4} align="end" side="bottom">
                <ExportOptions
                  onLocalDownload={() => downloadCSV(false)}
                  onMailExport={() => downloadCSV(true)}
                />
              </Dropdown.Content>
            </Dropdown.Portal>
          </Dropdown.Root>
        </div>
      </div>
      <div className="t-flex t-justify-between t-items-start">
        <Task1120TrackerFilter
          values={values}
          updateFilter={resetPageAndUpdateFilter}
        />
        <div className="t-flex t-flex-col t-gap-2 t-shrink-0">
          <PaginationHandler
            page_num={page}
            per_page={itemsPerPage}
            total_count={totalItemCount}
            total_pages={totalPage}
            showOptions
          />
          <div className="t-w-full t-flex t-justify-end t-items-center t-gap-4">
            {showSavedMessage && (
              <Async.Root
                isLoading={isUpdating}
                customLoader={
                  <div className="t-flex t-gap-1 t-items-center">
                    <Loader customType="primary" size="small" />
                    <div className="t-text-body-sm t-text-text-30">
                      Updating results...
                    </div>
                  </div>
                }
                isEmpty={false}
                isSuccess={isSuccess}
              >
                <Async.Empty>
                  <></>
                </Async.Empty>
                <Async.Success>
                  <span className="t-flex t-items-center t-gap-2 t-transition-all t-text-body-sm t-text-text-30">
                    <img src={DoubleGreenTick} alt="Saved" />
                    Results updated
                  </span>
                </Async.Success>
              </Async.Root>
            )}
            {isAnyFilterActive && (
              <Button size="small" onClick={resetFilter}>
                Clear all filters
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
